'use client'

import { useState, useEffect, useCallback } from 'react'
import { useOptimizedCartStore } from '@/stores/optimized-cart-store'

/**
 * CLS-safe cart notifications that don't cause layout shifts
 * Uses fixed positioning and transforms to avoid affecting document flow
 */

interface CartNotification {
  id: string
  type: 'add' | 'remove' | 'update' | 'error'
  message: string
  productName?: string
  quantity?: number
  timestamp: number
}

export default function CLSSafeCartNotifications() {
  const [notifications, setNotifications] = useState<CartNotification[]>([])
  const items = useOptimizedCartStore(state => state.items)

  // Track previous items to detect changes
  const [prevItemsCount, setPrevItemsCount] = useState(0)

  // Add notification without causing layout shift
  const addNotification = useCallback((notification: Omit<CartNotification, 'id' | 'timestamp'>) => {
    const newNotification: CartNotification = {
      ...notification,
      id: Math.random().toString(36).substr(2, 9),
      timestamp: Date.now()
    }

    setNotifications(prev => [...prev, newNotification])

    // Auto-remove after 3 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== newNotification.id))
    }, 3000)
  }, [])

  // Monitor cart changes and show notifications
  useEffect(() => {
    const currentCount = items.length

    if (prevItemsCount === 0 && currentCount > 0) {
      // Initial load - don't show notification
      setPrevItemsCount(currentCount)
      return
    }

    if (currentCount > prevItemsCount) {
      addNotification({
        type: 'add',
        message: 'Item added to cart'
      })
    } else if (currentCount < prevItemsCount) {
      addNotification({
        type: 'remove',
        message: 'Item removed from cart'
      })
    }

    setPrevItemsCount(currentCount)
  }, [items.length, prevItemsCount, addNotification])

  if (notifications.length === 0) {
    return null
  }

  return (
    <div 
      className="fixed top-20 right-4 z-[110] space-y-2 pointer-events-none"
      role="status"
      aria-live="polite"
      aria-label="Cart notifications"
    >
      {notifications.map((notification, index) => (
        <div
          key={notification.id}
          className={`
            transform transition-all duration-300 ease-out
            ${index === notifications.length - 1 ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
            bg-white/90 backdrop-blur-md border border-gray-200 
            rounded-lg shadow-lg px-4 py-3 max-w-sm
            pointer-events-auto
          `}
          style={{
            transform: `translateY(${index * 60}px) translateX(${index === notifications.length - 1 ? '0' : '100%'})`,
            transitionDelay: `${index * 100}ms`
          }}
        >
          <div className="flex items-center space-x-3">
            {/* Icon based on notification type */}
            <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
              notification.type === 'add' ? 'bg-green-100 text-green-600' :
              notification.type === 'remove' ? 'bg-red-100 text-red-600' :
              notification.type === 'update' ? 'bg-blue-100 text-blue-600' :
              'bg-yellow-100 text-yellow-600'
            }`}>
              {notification.type === 'add' && (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              )}
              {notification.type === 'remove' && (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              )}
              {notification.type === 'update' && (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              )}
              {notification.type === 'error' && (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
            </div>

            {/* Message */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">
                {notification.message}
              </p>
              {notification.productName && (
                <p className="text-xs text-gray-500 truncate">
                  {notification.productName}
                  {notification.quantity && ` (${notification.quantity})`}
                </p>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * Hook to manually trigger cart notifications
 */
export function useCartNotifications() {
  const [notifications, setNotifications] = useState<CartNotification[]>([])

  const addNotification = useCallback((notification: Omit<CartNotification, 'id' | 'timestamp'>) => {
    const newNotification: CartNotification = {
      ...notification,
      id: Math.random().toString(36).substr(2, 9),
      timestamp: Date.now()
    }

    setNotifications(prev => [...prev, newNotification])

    // Auto-remove after 3 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== newNotification.id))
    }, 3000)
  }, [])

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id))
  }, [])

  const clearAllNotifications = useCallback(() => {
    setNotifications([])
  }, [])

  return {
    notifications,
    addNotification,
    removeNotification,
    clearAllNotifications
  }
}

/**
 * CLS-safe cart count badge that maintains consistent dimensions
 */
export function CLSSafeCartBadge({ 
  count, 
  className = '',
  size = 'md' 
}: { 
  count: number
  className?: string
  size?: 'sm' | 'md' | 'lg'
}) {
  const sizeClasses = {
    sm: 'h-4 w-4 text-xs',
    md: 'h-5 w-5 text-xs',
    lg: 'h-6 w-6 text-sm'
  }

  // Always render the badge container to maintain layout stability
  return (
    <span 
      className={`
        absolute -top-1 -right-1 
        ${sizeClasses[size]}
        bg-primary text-bg-main font-bold rounded-full 
        flex items-center justify-center
        transition-all duration-200 ease-out
        ${count > 0 ? 'scale-100 opacity-100' : 'scale-0 opacity-0'}
        ${className}
      `}
      aria-label={count > 0 ? `${count} items in cart` : 'Cart is empty'}
    >
      {count > 0 && (
        <span className="leading-none">
          {count > 99 ? '99+' : count}
        </span>
      )}
    </span>
  )
}

/**
 * CLS-safe loading state for cart operations
 */
export function CLSSafeCartLoading({ 
  isLoading, 
  children,
  loadingHeight = 'auto',
  className = ''
}: {
  isLoading: boolean
  children: React.ReactNode
  loadingHeight?: string | number
  className?: string
}) {
  const heightStyle = typeof loadingHeight === 'number' 
    ? { height: `${loadingHeight}px` } 
    : { height: loadingHeight }

  return (
    <div className={`relative ${className}`} style={heightStyle}>
      {/* Loading overlay */}
      <div 
        className={`
          absolute inset-0 bg-white/80 backdrop-blur-sm 
          flex items-center justify-center
          transition-opacity duration-200
          ${isLoading ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
        `}
      >
        <div className="flex items-center space-x-2">
          <svg 
            className="animate-spin h-5 w-5 text-primary" 
            xmlns="http://www.w3.org/2000/svg" 
            fill="none" 
            viewBox="0 0 24 24"
          >
            <circle 
              className="opacity-25" 
              cx="12" 
              cy="12" 
              r="10" 
              stroke="currentColor" 
              strokeWidth="4"
            />
            <path 
              className="opacity-75" 
              fill="currentColor" 
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span className="text-sm text-gray-600">Updating cart...</span>
        </div>
      </div>

      {/* Content */}
      <div className={isLoading ? 'pointer-events-none' : ''}>
        {children}
      </div>
    </div>
  )
}